import { CompanyFeature } from '../types/CompanyFeature'
import { FeatureType } from '../types/Feature'
import { MenuItem } from '../types/MenuItem'
import { FeatureFlagManager } from './featureflags'

type MenuDefinition = Partial<Record<FeatureType, MenuItem>>

const Seller: MenuDefinition = {
	'seller-copilot': {
		label: 'Copiloto do Vendedor',
		isSection: true,
		children: [
			{
				label: 'Pesquisa por Loja',
				to: '/copiloto-do-vendedor/pesquisa',
			},
			{
				label: 'Venda AI',
				to: '/copiloto-do-vendedor/venda-ai',
			},
		],
	},
}

const Campaigns: MenuDefinition = {
	'campaign-simulator': {
		label: 'Simulador de Campanha',
		to: '/sales/simulador-campanha',
	},
}

const AlertMonitor: MenuDefinition = {
	'alert-monitor': {
		label: 'Monitor de Alertas',
		children: [
			{
				label: 'Lista de alertas',
				to: '/alerts/list',
			},
			{
				label: 'Gerar novo alerta',
				to: '/alerts/new',
			},
		],
	},
}

const Lojistas: MenuDefinition = {
	'lojistas-visao-geral': {
		label: 'Visão Geral',
		to: '/lojistas/geral',
		hasBullet: true
	},
	'lojistas-tabela-reposicao': {
		label: 'Tabela de Reposição',
		to: '/lojistas/tabela-reposicao',
		hasBullet: true
	},
	'lojistas-pedido-compra': {
		label: 'Pedido de Compra',
		to: '/lojistas/pedido-compra',
		hasBullet: true
	},
	'lojistas-importar-dados': {
		label: 'Importar Dados',
		to: '/lojistas/importar',
		hasBullet: true
	},
	'lojistas-configuracoes': {
		label: 'Configurações',
		to: '/lojistas/configuracoes',
		hasBullet: true
	}
}

const Reports: MenuDefinition = {
	'pedidos-report': {
		label: 'Pedidos',
		to: '/reports/pedidos',
	},
	'promocoes-report': {
		label: 'Promoções',
		to: '/reports/promos',
	},
	'notafiscal-report': {
		label: 'Nota Fiscal',
		to: '/reports/notafiscal',
	},
	'cadastrolojistas-report': {
		label: 'Cadastro Lojistas',
		to: '/reports/cadastro_lojistas',
	},
	'vendedordistribuidor-report': {
		label: 'Cadastro Vendedores',
		to: '/reports/cadastro_vendedores',
	},
	'apuracao-report': {
		label: 'Apuração',
		to: '/reports/apuracao',
	},
	'sellout-report': {
		label: 'Sellout',
		to: '/reports/sellout',
	},
	'historico-preco-estoque-report': {
		label: 'Histórico Preço Estoque',
		to: '/reports/historico_preco_estoque',
	},
	'distribuidor-prioritario-report': {
		label: 'Distribuidor Prioritário',
		to: '/reports/distribuidor_prioritario',
	},
	'ultimo-preco-estoque-report': {
		label: 'Último Preço Estoque',
		to: '/reports/ultimo_preco_estoque',
	},
	'lojistas-report': {
		label: 'Cashback Lojistas',
		to: '/reports/lojistas',
	},
	'cupons-report': {
		label: 'Cashback Cupons',
		to: '/reports/cupons',
	},
	'politica-preco-report': {
		label: 'Política de Preço',
		to: '/reports/politica_preco',
	},
	'lojas-venda-premiada-report': {
		label: 'Lojas Venda Premiada',
		to: '/reports/lojas_venda_premiada',
	},
}

const getItems = (features: CompanyFeature[] | '*', menuData: MenuDefinition): MenuItem[] => {
	if (features === '*') return Object.values(menuData).filter(Boolean)
	const featureSet:any = new Set(features.map((f) => f.name))

	return Object.entries(menuData).reduce<MenuItem[]>((acc, [featureName, item]) => {
		if (!item) return acc
		if (!featureSet.has(featureName)) return acc
		if (item.children?.length) {
			const filteredChildren = item.children.filter((child) => !!child.to)
			if (!filteredChildren.length) {
				return acc
			}
			acc.push({
				...item,
				children: filteredChildren,
			})
		} else {
			acc.push(item)
		}
		return acc
	}, [])
}

export const getCampaignsMenu = (companyFeatures: CompanyFeature[]): MenuItem[] => {
	const items = getItems(companyFeatures, Campaigns).sort((a, b) =>
		a.label.localeCompare(b.label),
	)
	if (!items.length) return []
	return [
		{
			label: 'Produtos de dados',
			isSection: true,
			hasBullet: false,
			children: items,
		},
	]
}

export const getSellerMenu = (): MenuItem[] => {
	const items = getItems([], Seller)
	return items
}

export const getLojistasrMenu = (features:any): MenuItem[] => {
	const items = getItems(features, Lojistas)
	return items
}

export const getToolsMenu = (companyFeatures: CompanyFeature[], isSuperAdmin: boolean): MenuItem[] => {
	const featuresOrAll = isSuperAdmin ? '*' : companyFeatures

	const alertMonitor = getItems(featuresOrAll, AlertMonitor)
	const reports = getItems(featuresOrAll, Reports).sort((a, b) =>
		a.label.localeCompare(b.label)
	)
	const copilot = getItems(featuresOrAll, Seller)
	const superAdminItems: MenuItem[] = isSuperAdmin
		? [
			{
				label: 'SUPER ADMIN',
				isSection: true,
				hasBullet: false,
				children: [
					{
						label: 'Sistema de Logs',
						to: '/logs',
					},
				],
			},
		]
		: []

	const items: MenuItem[] = [
		...alertMonitor,
		{
			label: 'Relatórios',
			hasBullet: false,
			children: reports,
		},
		...copilot,
		...superAdminItems,
	].filter((menuItem) => {
		if (menuItem.children && !menuItem.children.length) {
			return false
		}
		return true
	})

	if (items.length === 1 && items[0].label === 'Copiloto do Vendedor') {
		return items
	}

	if (!items.length) {
		return []
	}

	return [
		{
			label: 'Ferramentas',
			isSection: true,
			hasBullet: false,
			children: items,
		},
	]
}
